import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings as Palette, QrCode, Copy, Download, Share2, ImageIcon } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import ImagePicker from "@/components/homepage/ImagePicker";
import ThemePreviewModal, { ThemeThumb } from "@/components/app/ThemePreview";
import { Field, useValidatedForm, isBlank } from "@/components/app/FormKit";
import { btn, btnPrimary, cardCls, pageTitle } from "@/components/app/ui";

const CURRENCIES = [{ value: "INR", label: "INR — Indian Rupee" }, { value: "USD", label: "USD — US Dollar" }, { value: "EUR", label: "EUR — Euro" }, { value: "GBP", label: "GBP — British Pound" }];

export default function Settings() {
  const [store, setStore] = useState(null);
  const [themes, setThemes] = useState([]);
  const [qr, setQr] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logo, setLogo] = useState("");
  const [savingLogo, setSavingLogo] = useState(false);
  const [favicon, setFavicon] = useState("");
  const [savingFav, setSavingFav] = useState(false);

  const validate = useCallback((f) => {
    const e = {};
    if (isBlank(f.name)) e.name = "Store name is required.";
    else if (f.name.trim().length < 2) e.name = "Store name must be at least 2 characters.";
    else if (f.name.trim().length > 60) e.name = "Store name must be 60 characters or fewer.";
    if (f.tagline.length > 100) e.tagline = "Tagline must be 100 characters or fewer.";
    if (isBlank(f.currency)) e.currency = "Choose a currency.";
    if (!isBlank(f.pincode) && !/^[1-9]\d{5}$/.test(f.pincode.trim())) e.pincode = "Enter a valid 6-digit pincode.";
    if (isBlank(f.free_shipping_over)) e.free_shipping_over = "Enter an amount, or 0 for no free delivery.";
    else if (!(Number(f.free_shipping_over) >= 0) || Number(f.free_shipping_over) > 1000000) e.free_shipping_over = "Enter an amount between 0 and 10,00,000.";
    return e;
  }, []);
  const form = useValidatedForm({ name: "", tagline: "", currency: "INR", pincode: "", free_shipping_over: "999" }, validate);

  useEffect(() => {
    api.get("/themes").then(({ data }) => setThemes(data));
    api.get("/vendor/store/qr").then(({ data }) => setQr(data)).catch(() => {});
    api.get("/vendor/store").then(({ data }) => { setStore(data); setLogo(data.logo || ""); setFavicon(data.favicon || ""); form.reset({ name: data.name, tagline: data.tagline || "", currency: data.currency || "INR", pincode: data.pincode || "", free_shipping_over: String(data.free_shipping_over ?? 999) }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!form.check()) return;
    setSaving(true);
    try {
      const { data } = await api.put("/vendor/store", { ...form.values, name: form.values.name.trim(), free_shipping_over: Number(form.values.free_shipping_over) });
      setStore(data); toast.success("Store settings saved successfully.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveLogo = async () => {
    setSavingLogo(true);
    try {
      const { data } = await api.put("/vendor/store/logo", { logo });
      setStore((s) => ({ ...s, logo: data.logo })); setLogo(data.logo);
      toast.success(data.logo ? "Store logo updated. It now shows on your storefront." : "Store logo removed.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSavingLogo(false); }
  };
  const saveFavicon = async () => {
    setSavingFav(true);
    try {
      const { data } = await api.put("/vendor/store/favicon", { favicon });
      setStore((s) => ({ ...s, favicon: data.favicon })); setFavicon(data.favicon);
      toast.success(data.favicon ? "Favicon updated. It now shows in browser tabs for your storefront." : "Favicon removed.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSavingFav(false); }
  };
  const applyTheme = async (t) => {
    try { await api.put("/vendor/store/theme", t); setStore({ ...store, theme: t }); toast.success(`${t.name} theme applied to your storefront.`); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  if (!store) return <div className="text-slate-500 text-[13px]">Loading…</div>;

  return (
    <div className="space-y-4 max-w-6xl" data-testid="vendor-settings">
      <h1 className={pageTitle}>Store Settings</h1>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
      <div className="space-y-4 min-w-0">

      <div className={`${cardCls} p-5`}>
        <div className="grid grid-cols-6 gap-x-3 gap-y-3">
          <Field className="col-span-6 sm:col-span-2" label="Store name" required maxLength={60} t="settings-name" {...form.bind("name")} />
          <Field className="col-span-6 sm:col-span-3" label="Tagline" maxLength={100} t="settings-tagline" placeholder="A short line about your store" {...form.bind("tagline")} />
          <Field className="col-span-6 sm:col-span-1" label="Currency" required type="select" options={CURRENCIES} t="settings-currency" {...form.bind("currency")} />
          <Field className="col-span-6 sm:col-span-2" label="Pickup pincode" maxLength={6} inputMode="numeric" t="settings-pincode" placeholder="6-digit pincode" hint="Where your parcels are collected. Used to calculate delivery charges at checkout." {...form.bind("pincode")} />
          <Field className="col-span-6 sm:col-span-2" label="Free delivery over" type="number" min="0" step="1" prefix="₹" t="settings-free-shipping" placeholder="999" hint="Orders at or above this subtotal ship free. Enter 0 to turn it off." {...form.bind("free_shipping_over")} />
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <div className="text-[13px] text-slate-500">Store URL: <span className="font-mono text-orange-600">sell24x7.com/store/{store.slug}</span></div>
          <button onClick={save} disabled={saving} data-testid="settings-save" className={btnPrimary}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      </div>

      {qr && (
        <div className={`${cardCls} p-5`} data-testid="store-qr-card">
          <div className="flex items-center gap-2 mb-3"><QrCode size={16} className="text-orange-600" /><h2 className="text-[14px] font-bold">Store QR & Sharing</h2></div>
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <img src={qr.qr} alt="Store QR" className="w-32 h-32 rounded-lg border border-slate-200 bg-white p-1.5" data-testid="store-qr-image" />
            <div className="flex-1 w-full">
              <div className="text-[13px] text-slate-500 mb-1.5">Share your store anywhere:</div>
              <div className="flex items-center gap-2 rounded-lg bg-white border border-slate-300 px-3 h-9 mb-2.5">
                <span className="font-mono text-[12px] text-orange-600 truncate flex-1">{qr.url}</span>
                <button onClick={() => { navigator.clipboard.writeText(qr.url); toast.success("Store link copied."); }} className="text-slate-500 hover:text-blue-600" data-testid="store-copy-link" aria-label="Copy link"><Copy size={14} /></button>
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`https://wa.me/?text=${encodeURIComponent("Check out my store: " + qr.url)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
                  className={`${btn} bg-green-50 text-green-700 hover:bg-green-100`}><Share2 size={13} /> WhatsApp</a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(qr.url)}`} target="_blank" rel="noreferrer"
                  className={`${btn} bg-blue-50 text-blue-700 hover:bg-blue-100`}><Share2 size={13} /> Facebook</a>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(qr.url)}`} target="_blank" rel="noreferrer"
                  className={`${btn} bg-slate-100 text-neutral-800 hover:bg-slate-200`}><Share2 size={13} /> X</a>
                <a href={qr.qr} download="store-qr.png" data-testid="store-qr-download"
                  className={`${btn} bg-orange-50 text-orange-700 hover:bg-orange-100`}><Download size={13} /> QR</a>
              </div>
            </div>
          </div>
        </div>
      )}


      <div className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-3"><Palette size={16} className="text-orange-600" /><h2 className="text-[14px] font-bold">Pre-built themes</h2></div>
        <div className="grid sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {themes.map((t) => (
            <button key={t.id} onClick={() => applyTheme(t)} data-testid={`settings-theme-${t.id}`}
              className={`text-left rounded-lg border-2 overflow-hidden transition-all ${store.theme?.id === t.id ? "border-blue-600 shadow-md" : "border-slate-200 hover:border-blue-300"}`}
              style={{ background: t.colors.surface }}>
              <ThemeThumb t={t} height={t.preview_image ? 110 : 40} onPreview={setViewing} />
              <div className="px-2.5 py-2 text-[12px] font-semibold" style={{ color: t.colors.text }}>{t.name}</div>
            </button>
          ))}
        </div>
      </div>
      </div>

      <div className="space-y-4 lg:sticky lg:top-4">
      <aside className={`${cardCls} p-5`} data-testid="store-logo-card">
        <div className="flex items-center gap-2 mb-1"><ImageIcon size={16} className="text-orange-600" /><h2 className="text-[14px] font-bold">Store logo</h2></div>
        <p className="text-[12px] text-slate-500 mb-3">Shown in the header and footer of your storefront, in every theme. A wide, transparent PNG works best.</p>
        <ImagePicker value={logo} onChange={setLogo} aspect="aspect-[3/2]" fit="contain" t="store-logo-upload" hint="Recommended: at least 400 px wide." />
        {logo && (
          <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] text-slate-500" aria-label="Logo preview">
            <div className="rounded-lg border border-slate-200 bg-white p-2 flex flex-col items-center gap-1"><img src={logo} alt="" className="h-8 max-w-full object-contain" />Light header</div>
            <div className="rounded-lg p-2 flex flex-col items-center gap-1" style={{ background: "#0f2e1a", color: "#c9dcc0" }}><img src={logo} alt="" className="h-6 max-w-full object-contain" />Dark header</div>
          </div>
        )}
        <button onClick={saveLogo} disabled={savingLogo || logo === (store.logo || "")} data-testid="store-logo-save" className={`${btnPrimary} w-full mt-4`}>{savingLogo ? "Saving…" : logo ? "Save logo" : store.logo ? "Remove logo" : "Save logo"}</button>
      </aside>

      <aside className={`${cardCls} p-5`} data-testid="store-favicon-card">
        <div className="flex items-center gap-2 mb-1"><ImageIcon size={16} className="text-orange-600" /><h2 className="text-[14px] font-bold">Favicon</h2></div>
        <p className="text-[12px] text-slate-500 mb-3">The small icon shown in the browser tab, bookmarks and search results for your store.</p>
        <ImagePicker value={favicon} onChange={setFavicon} aspect="aspect-square" fit="contain" t="store-favicon-upload" hint="Use a square image, at least 64 × 64 px (PNG works best)." />
        {favicon && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-600" aria-label="Browser tab preview">
            <img src={favicon} alt="" className="w-4 h-4 object-contain" /><span className="truncate">{(form.values.name || store.name)}{form.values.tagline ? ` – ${form.values.tagline}` : ""}</span>
          </div>
        )}
        <button onClick={saveFavicon} disabled={savingFav || favicon === (store.favicon || "")} data-testid="store-favicon-save" className={`${btnPrimary} w-full mt-4`}>{savingFav ? "Saving…" : favicon ? "Save favicon" : store.favicon ? "Remove favicon" : "Save favicon"}</button>
      </aside>
      </div>
      </div>
      <ThemePreviewModal theme={viewing} onClose={() => setViewing(null)} onUse={applyTheme} selected={viewing && store.theme?.id === viewing.id} />
    </div>
  );
}
